'use client'

import type { Route } from 'next'
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { navigateWithinSite } from '@/lib/analytics/script-boundary'
import { AuthModal, type AuthModalCampaign } from './auth-modal'
import type { OtpVerifyResponse } from './auth-storage'
import { DEFAULT_POST_AUTH_DESTINATION } from './post-auth-destination'

/**
 * What the completed sign-in turned out to be. Passed to the opener's
 * continuation so it can tell a signup from a returning sign-in — the two are
 * indistinguishable from inside the modal, which runs one unified flow.
 */
export interface AuthOutcome {
  /**
   * Whether this verify is the call that brought the account into being.
   * `undefined` when the API did not say (an older deployment); a caller must
   * then fall back to its own knowledge rather than assuming either answer.
   */
  accountWasCreated?: boolean
}

interface OpenAuthModalOptions {
  /**
   * Where to go once sign-in succeeds:
   *  - omitted → the default landing (`/app/transcriptions`, the transcripts list)
   *  - `null` → STAY on the current page (refresh only). Used by in-place
   *    guest gating (auth-guard, transcripts) so a guest who signs in on a
   *    protected page re-renders authed instead of being bounced away.
   *  - a path → navigate there.
   */
  redirectTo?: Route | null
  /** Pre-fill the email field — e.g. the address a guest already typed at
   *  checkout that turned out to belong to an existing account. */
  prefillEmail?: string
  /** Optional explanatory banner shown atop the modal — e.g. "this email
   *  already has an account, sign in to finish your purchase". */
  notice?: string
  /**
   * Runs exactly once after a successful verify, when the session token is
   * already stored and the modal has closed. The public campaign banner uses
   * it to fire its claim as the freshly signed-in user without leaving the
   * page. Cleared on close, so a dismissed modal never calls back.
   */
  onAuthenticated?: (outcome: AuthOutcome) => void
  /** Opens the campaign variant of the dialog (see {@link AuthModalCampaign}). */
  campaign?: AuthModalCampaign
}

interface AuthModalContextValue {
  /** Open the unified login/signup modal. */
  open: (opts?: OpenAuthModalOptions) => void
  /** Close the modal — equivalent to clicking the X (works at any step). */
  close: () => void
  /** Whether the modal is currently visible. */
  isOpen: boolean
}

const AuthModalContext = createContext<AuthModalContextValue | null>(null)

/**
 * Mount once near the top of the tree (inside the i18n provider but outside
 * any individual page). Pages and components call `useAuthModal().open()` to
 * trigger sign-in without leaving the current page — this fully replaced the
 * dedicated /login page (now removed); guest gating opens the modal in place.
 *
 * On successful auth we call `router.refresh()` so server components
 * (notably AccountSummaryPanel which reads the stored token client-side
 * but the layout's locale-resolution + any RSC data sources may want a
 * fresh pass) re-render with the new auth state. Components that hold
 * their own auth-dependent state via `useState` will not auto-refresh —
 * those should subscribe to a re-render trigger (event / SWR mutation /
 * `useSyncExternalStore`) of their own.
 */
export function AuthModalProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const [prefillEmail, setPrefillEmail] = useState<string | undefined>(undefined)
  const [notice, setNotice] = useState<string | undefined>(undefined)
  const [campaign, setCampaign] = useState<AuthModalCampaign | undefined>(undefined)
  // A ref for the same reason as `redirectAfterAuth`: read once inside the
  // one-shot auth callback, and must not re-run effects when it changes.
  const onAuthenticatedRef = useRef<((outcome: AuthOutcome) => void) | undefined>(undefined)
  // Post-auth destination chosen by the opener (see OpenAuthModalOptions).
  // A ref, not state — the value is only read inside the one-shot auth
  // callback and must not re-run effects / re-memoize when it changes.
  const redirectAfterAuth = useRef<Route | null | undefined>(undefined)

  const open = useCallback((opts?: OpenAuthModalOptions) => {
    redirectAfterAuth.current = opts?.redirectTo
    onAuthenticatedRef.current = opts?.onAuthenticated
    setPrefillEmail(opts?.prefillEmail)
    setNotice(opts?.notice)
    setCampaign(opts?.campaign)
    setIsOpen(true)
  }, [])
  // Single close path (X / backdrop / post-auth all route through here) — also
  // drops any prefilled email / notice so one opener's state can't linger.
  const handleOpenChange = useCallback((next: boolean) => {
    setIsOpen(next)
    if (!next) {
      setPrefillEmail(undefined)
      setNotice(undefined)
      setCampaign(undefined)
      // The continuation is deliberately NOT cleared here. AuthModal closes
      // itself BEFORE reporting a successful verify, so clearing on close would
      // drop the callback of every successful auth. It is instead overwritten by
      // the next open() (to that opener's value, or undefined) and cleared once
      // handleAuthenticated has run it — so a dismissed modal still cannot
      // resurrect it.
    }
  }, [])
  const close = useCallback(() => handleOpenChange(false), [handleOpenChange])

  const handleAuthenticated = useCallback(
    (result: OtpVerifyResponse) => {
      // Trigger a server-state refresh so any RSC tree (and the
      // `router`-keyed client cache) picks up the new auth. In-place guest
      // gating relies on this alone (redirectTo === null) to re-render authed.
      router.refresh()
      // The opener's continuation runs before any navigation: an in-place
      // opener (redirectTo === null) needs it, and a navigating one would
      // otherwise have its callback raced by the route change.
      const continueWith = onAuthenticatedRef.current
      onAuthenticatedRef.current = undefined
      continueWith?.({ accountWasCreated: result.accountWasCreated })
      const dest = redirectAfterAuth.current
      if (dest === null) return // stay on the current page
      // Default landing after sign-in is the transcripts list (uploading is a
      // modal there now); an explicit path overrides it. The modal opens on
      // marketing pages that run Clarity, which must not follow into `/app`.
      const target: Route = dest ?? DEFAULT_POST_AUTH_DESTINATION
      navigateWithinSite(target, () => router.push(target))
    },
    [router]
  )

  const value = useMemo<AuthModalContextValue>(
    () => ({ open, close, isOpen }),
    [open, close, isOpen]
  )

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <AuthModal
        open={isOpen}
        onOpenChange={handleOpenChange}
        onAuthenticated={handleAuthenticated}
        prefillEmail={prefillEmail}
        notice={notice}
        campaign={campaign}
      />
    </AuthModalContext.Provider>
  )
}

const NO_OP_AUTH_MODAL: AuthModalContextValue = {
  open: () => {},
  close: () => {},
  isOpen: false,
}

export function useAuthModal(): AuthModalContextValue {
  // Return a no-op fallback when called outside the provider (e.g. in
  // unit tests that render a single client component without mounting
  // the root layout). The real app always mounts AuthModalProvider in
  // the root layout, so production paths are unaffected.
  return useContext(AuthModalContext) ?? NO_OP_AUTH_MODAL
}
